import * as React from "react";
import { useDropzone } from "react-dropzone";
import { Storage } from "aws-amplify";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";
import { toast } from "react-toastify";
import * as _ from "lodash";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text/Text";
import { Button } from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import { OrderImportFileType } from "src/modules/order-import/api/order-import.api";

interface DropZoneProps {
  s3Path: string;
  dropzoneMessage: string;
  processFile: (key: string) => void;
  loading: boolean;
  error?: string | null;
  fileType: OrderImportFileType;
}
export const DropZone: React.ReactFCWithChildren<DropZoneProps> = props => {
  const [ uploadingFile, setUploadingFile ] = React.useState<boolean | null>(null);
  const [ fileErrors, setFileErrors ] = React.useState<string[] | null>(null);
  const [ error, setError ] = React.useState<string | null | undefined>(null);
  const [ key, setKey ] = React.useState<string | null>(null);
  const [ fileName, setFileName ] = React.useState<string | null>(null);

  React.useEffect(() => {
    setError(props.error);
  }, [ props.error ]);

  // reset the dropzone when the s3 path changes
  React.useEffect(() => {
    if (props.s3Path) {
      setKey(null);
      setUploadingFile(null);
    }
  }, [ props.s3Path ]);

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    const reader = new FileReader();

    // extend FileReader for IE 11 :roll_eyes:
    if (!FileReader.prototype.readAsBinaryString) {
      FileReader.prototype.readAsBinaryString = function (fileData: File) {
        let binary = "";
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const pt = this;
        const reader = new FileReader();

        reader.onload = () => {
          if (reader.result) {
            // @ts-ignore
            const bytes = new Uint8Array(reader.result);
            const length = bytes.byteLength;

            for (let i = 0; i < length; i++) {
              binary += String.fromCharCode(bytes[ i ]);
            }

            // pt.result  - readonly so assign binary
            // @ts-ignore
            pt.content = binary;

            if (pt.onload) {
              // @ts-ignore
              pt.onload();
            }
          }
        };
        reader.readAsArrayBuffer(fileData);
      };
    }

    setUploadingFile(true);

    reader.onabort = () => {
      setFileErrors([ "Error parsing the file. Aborted." ]);
    };

    reader.onerror = error => {
      setFileErrors([ `Error parsing the file ${error}` ]);
    };

    reader.onload = async e => {
      // @ts-ignore
      const binaryStr = !e ? reader.content : (e.target.result as string);

      try {
        let contentType = "text/csv";
        let fileExtension = "csv";

        if (props.fileType === OrderImportFileType.XLSX) {
          contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          fileExtension = "xlsx";
        } else if (props.fileType === OrderImportFileType.XML) {
          contentType = "text/xml";
          fileExtension = "xml";
        }

        const result: any = await Storage.put(`${props.s3Path}/${moment.utc().format("DD-MM-YYYY:HH:mm:ss")}.${fileExtension}`, binaryStr, {
          level: "public",
          contentType,
          contentEncoding: "base64"
        });

        setUploadingFile(false);
        setKey(`public/${result.key}`);
      } catch (error) {
        toast.error("Unable to upload file.");
        setUploadingFile(null);
        console.error(error);
        setFileErrors([ "Could not upload file via DropZone Component" ]);
      }
    };

    acceptedFiles.forEach((file: File) => {
      setFileName(file.name);

      if (props.fileType === OrderImportFileType.XLSX || props.fileType === OrderImportFileType.XML) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file, "utf-8");
      }
    });
  }, [ props.s3Path, props.fileType ]);

  const acceptedFileTypes = React.useMemo(() => {
    if (props.fileType === OrderImportFileType.XLSX) {
      return [ "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel" ];
    } else if (props.fileType === OrderImportFileType.CSV) {
      return [
        ".csv",
        "text/csv",
        "application/vnd.ms-excel"
      ];
    } else if (props.fileType === OrderImportFileType.XML) {
      return [ "text/xml" ];
    }

    return [];
  }, [ props.fileType ]);

  const {
    getRootProps, getInputProps, isDragActive
  } = useDropzone({
    onDrop,
    accept: acceptedFileTypes
  });

  if (error) {
    return (
      <SuccessResultContainer>
        <Text
          as="h4"
          color="error"
        >
          {props.error}
        </Text>

        <ButtonContainer>
          <Button
            loading={props.loading}
            onClick={() => {
              setFileErrors(null);
              setUploadingFile(null);
              setError(null);
            }}
          >
            Start again
          </Button>
        </ButtonContainer>
      </SuccessResultContainer>
    );
  }

  return (
    <>
      {uploadingFile === null && !fileErrors && (
        <DragAndDropContainer
          isDragActive={isDragActive}
          {..._.omit(getRootProps(), "isDragActive")}
        >
          <input {...getInputProps()} />

          <Icon
            icon={Icons.DOWNLOAD}
            $fillColor="primaryDark"
          />

          <Text as="h4">
            {props.dropzoneMessage}
          </Text>
        </DragAndDropContainer>
      )}

      {uploadingFile === false && !fileErrors && key && (
        <SuccessResultContainer>
          {!!fileName && (
            <Text as="h4">
              {fileName}
            </Text>
          )}

          <Text as="h4">
            The file was successfully uploaded and is ready to process
          </Text>

          <ButtonContainer>
            <Button
              loading={props.loading}
              onClick={() => props.processFile(key)}
            >
              Process file
            </Button>

            <Button
              $buttonType="error"
              borderColor="error"
              loading={props.loading}
              onClick={() => {
                setFileErrors(null);
                setUploadingFile(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </ButtonContainer>
        </SuccessResultContainer>
      )}
    </>
  );
};

const DragAndDropContainer = styled.div<{ isDragActive: boolean }>`
  ${({ theme, isDragActive }) => css`
    text-align: center;
    padding: 50px ${theme.spacing.padding.md};
    border: dashed 2px ${isDragActive ? theme.colors.primaryLight : theme.colors.primaryLight};
    border-radius: 4px;
    box-sizing: border-box;
  `}
`;

const SuccessResultContainer = styled.div`
  ${({ theme }) => css`
    position: relative;
    text-align: center;
    padding: 50px ${theme.spacing.padding.md};
    border: solid 2px ${theme.colors.primaryLight};
    border-radius: 4px;
    box-sizing: border-box;
  `}
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  button {
    margin-right: 10px;
  }
`;
